/**
 * Iter 67ek — Top 3 Partners mini-card on the home page.
 *
 * Renders between "Why book with RentalCar.al?" and the Google reviews
 * showcase. Pulls from `/api/public/affiliate-leaderboard` (same endpoint
 * as the `/widget-builder/top-partners` page) and shows the top 3 opt-in
 * affiliate profiles by confirmed bookings.
 *
 * Self-hides when no partners qualify so a quiet leaderboard never adds
 * empty whitespace to the home page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, ExternalLink, ArrowRight } from "lucide-react";

function _initials(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase())
    .slice(0, 2)
    .join("");
}

export default function TopPartnersMiniCard() {
  const [partners, setPartners] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/affiliate-leaderboard", { params: { days: 90, limit: 3 } })
      .then(({ data }) => { if (!cancelled) setPartners((data.partners || []).slice(0, 3)); })
      .catch(() => { if (!cancelled) setPartners([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  if (!loaded || partners.length === 0) return null;

  return (
    <section
      className="bg-gradient-to-br from-amber-50/60 via-white to-amber-50/40 border-y border-amber-100/60 py-12"
      data-testid="top-partners-mini"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-[10px] font-bold uppercase tracking-[0.18em] mb-3">
              <Trophy className="h-3 w-3" /> Top partners
            </div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-semibold leading-tight">
              Travel sites picking <span className="text-amber-600">RentalCar.al</span>
            </h2>
            <p className="text-sm text-neutral-600 mt-1 max-w-md">
              These partner sites embedded our search widget and drove the most bookings in the last 90 days.
            </p>
          </div>
          <Link
            to="/widget-builder/top-partners"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-800 self-start md:self-auto"
            data-testid="top-partners-mini-cta"
          >
            See full leaderboard <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {partners.map((p, i) => (
            <article
              key={p.affiliate_id}
              className={`relative rounded-2xl border bg-white p-4 hover:shadow-md transition flex items-center gap-3 ${
                i === 0 ? "border-amber-300 ring-1 ring-amber-200" : "border-border"
              }`}
              data-testid={`top-partners-mini-card-${p.affiliate_id}`}
            >
              <div className="text-2xl font-display font-semibold w-8 text-center shrink-0">
                {i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}
              </div>

              {p.logo_url ? (
                <img
                  src={p.logo_url}
                  alt={p.display_name}
                  className="h-10 w-10 rounded-xl object-contain bg-neutral-50 p-1 shrink-0 ring-1 ring-neutral-200"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-white text-sm font-display font-semibold shrink-0">
                  {_initials(p.display_name)}
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate" data-testid="top-partners-mini-name">
                  {p.display_name}
                </div>
                {p.website ? (
                  <a
                    href={p.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-amber-700 hover:underline inline-flex items-center gap-1 mt-0.5"
                  >
                    {(() => {
                      try { return new URL(p.website).hostname; }
                      catch { return p.website; }
                    })()}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <div className="text-[11px] text-neutral-500 mt-0.5">{p.tagline || "Partner site"}</div>
                )}
              </div>

              <div className="text-right shrink-0">
                <div className="font-display text-lg font-semibold text-amber-600 tabular-nums leading-none">
                  {p.confirmed_bookings}
                </div>
                <div className="text-[9px] uppercase tracking-wider font-bold text-neutral-500">
                  bookings
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
